import { useLoaderData } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import BrowserTimezoneAlert from "components/BrowserTimezoneAlert";
import EmptyStatePlaceholder from "components/EmptyStatePlaceholder";
import FilterSection from "components/FilterSection";
import LoadingStateProgressBar from "components/LoadingStateProgressBar";
import PageHeader from "components/PageHeader";
import SearchParamControlledAutocomplete from "components/SearchParamControlledAutocomplete";
import StickyTableCell from "components/StickyTableCell";
import SyncRunStatus from "components/SyncRunStatus";
import TableContainerWithScrollShadow from "components/TableContainerWithScrollShadow";

import { getCronDescriptionWithTzOffset } from "helpers/cron";
import { capitalizeFirstChar } from "helpers/string";
import { formatTimestamp } from "helpers/datetime";
import buildRoute from "../components/buildRoute";

function getUniqueValuesOfKeyFromObjectList(list, key) {
  return [...new Set(list.map((item) => item[key]).filter(Boolean))];
}

function SyncSchedulePage() {
  const { syncs, allVendorNames, allPartners } = useLoaderData();

  const firstHeaderCellStyles = { minWidth: 200, zIndex: 10, borderBottom: "none" };
  const headerCellStyles = { ...firstHeaderCellStyles, zIndex: 1, border: "none" };

  return (
    <>
      <PageHeader
        title="Sync Schedule"
        subtitle="View sync details across partners and platforms"
      />
      <Box
        component="section"
        sx={{ pt: 1, flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}
      >
        <BrowserTimezoneAlert sx={{ mb: 2 }} />
        <FilterSection sx={{ mb: 1, zIndex: 2 }}>
          <SearchParamControlledAutocomplete
            searchParam="vendor_name"
            label="Source"
            options={allVendorNames ?? []}
          />
          {/* Partner filter displays partner names and passes a slug to the query param */}
          <SearchParamControlledAutocomplete
            searchParam="partner"
            label="Partner"
            options={allPartners ?? []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.slug === value.slug}
          />
        </FilterSection>
        <LoadingStateProgressBar />

        {syncs.length ? (
          <TableContainerWithScrollShadow sx={{ flex: 1, maxHeight: "85vh" }}>
            <Table sx={{ minWidth: 650 }} stickyHeader aria-label="Syncs table">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={firstHeaderCellStyles}>Sync Name</StickyTableCell>
                  <StickyTableCell sx={headerCellStyles}>Partner</StickyTableCell>
                  <TableCell sx={headerCellStyles}>Source</TableCell>
                  <TableCell sx={headerCellStyles}>Schedule</TableCell>
                  <TableCell sx={headerCellStyles}>Last run status</TableCell>
                  <TableCell sx={headerCellStyles}>Last run end</TableCell>
                  <TableCell sx={headerCellStyles}>Next run</TableCell>
                  <TableCell sx={headerCellStyles}>Sync Type</TableCell>
                  <TableCell sx={headerCellStyles}>Sync ID</TableCell>
                  <TableCell sx={headerCellStyles}>Tags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncs.map((sync) => {
                  const tags = sync.additional_info?.dag?.tags;
                  return (
                    <TableRow key={sync.sync_id}>
                      <StickyTableCell>{sync.sync_name ?? "-"}</StickyTableCell>
                      <TableCell>
                        {Array.isArray(sync.partner_names)
                          ? sync.partner_names.sort().join(", ")
                          : sync.partner_names ?? "-"}
                      </TableCell>
                      <TableCell>{sync.vendor_name ?? "-"}</TableCell>
                      <TableCell>
                        {sync.schedule ? getCronDescriptionWithTzOffset(sync.schedule) : "-"}
                      </TableCell>
                      <TableCell><SyncRunStatus status={sync.latest_state} /></TableCell>
                      <TableCell>{formatTimestamp(sync.latest_execution_end_time) ?? "-"}</TableCell>
                      <TableCell>{formatTimestamp(sync.additional_info?.dag?.next_dagrun_data_interval_end) ?? "-"}</TableCell>
                      <TableCell>{sync.sync_type ? capitalizeFirstChar(sync.sync_type) : "-"}</TableCell>
                      <TableCell>{sync.sync_id ?? "-"}</TableCell>
                      <TableCell>
                        {tags?.length ? tags.map((t) => t.name).join(", ") : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainerWithScrollShadow>
        ) : (
          <EmptyStatePlaceholder
            title="No syncs to show"
            subtitle="Try adjusting your filters above."
          />
        )}
      </Box>
    </>
  );
}

const routeArgs = {
  element: <SyncSchedulePage />,
  loader: async ({ request }) => {
    const { searchParams } = new URL(request.url);
    const partner = searchParams.get("partner");
    const vendorName = searchParams.get("vendor_name");

    const queryParams = new URLSearchParams();
    if (partner) queryParams.set("partner", partner);
    if (vendorName) queryParams.set("vendor_name", vendorName);

    const syncsResp = await fetch(`/syncs/api/admin/syncs/?${queryParams}`);
    if (!syncsResp.ok) throw new Error(`HTTP error! status: ${syncsResp.status}`);
    const syncs = await syncsResp.json();

    const unfilteredResp = await fetch(`/syncs/api/admin/syncs/`);
    if (!unfilteredResp.ok) throw new Error(`HTTP error! status: ${unfilteredResp.status}`);
    const unfilteredSyncs = await unfilteredResp.json();

    const allVendorNames = getUniqueValuesOfKeyFromObjectList(unfilteredSyncs, "vendor_name").sort();
    const allPartners = Array.from(
      new Map(
        unfilteredSyncs
          .filter(s => s.partner_slugs && s.partner_names)
          .flatMap(s => s.partner_slugs.map((slug, idx) => [slug, { slug, name: s.partner_names[idx] }]))
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));

    return { syncs, allVendorNames, allPartners };
  },
};

export default buildRoute(routeArgs);
